import themesConfig from 'app/configs/themesConfig';
import { FuseSettingsConfigType } from '@fuse/core/FuseSettings/FuseSettings';

/**
 * Host-app skin selected via the `appearance` URL param (e.g. ?appearance=bc | scm),
 * so the embed matches the Dynamics product hosting it. Cosmetic only — never part
 * of the access token. Unknown or missing values fall back to the default look.
 */
export type AppearanceKey = 'bc' | 'scm';

const APPEARANCE_THEMES: Record<AppearanceKey, FuseSettingsConfigType['theme']> = {
	bc: {
		main: themesConfig.bc,
		navbar: themesConfig.bcDark,
		toolbar: themesConfig.bc,
		footer: themesConfig.bcDark
	},
	scm: {
		main: themesConfig.scm,
		navbar: themesConfig.scmDark,
		toolbar: themesConfig.scm,
		footer: themesConfig.scmDark
	}
};

export function getAppearance(): AppearanceKey | undefined {
	const value = new URLSearchParams(window.location.search).get('appearance')?.toLowerCase();
	return value && value in APPEARANCE_THEMES ? (value as AppearanceKey) : undefined;
}

export function getAppearanceTheme(): FuseSettingsConfigType['theme'] | undefined {
	const key = getAppearance();
	return key ? APPEARANCE_THEMES[key] : undefined;
}

/**
 * Fluent-style tokens shared by both Dynamics skins — Segoe UI and the Fluent
 * neutral ink ramp — so the embed reads as a native Dynamics panel.
 */
const SHARED_FLUENT_VARS: Record<string, string> = {
	'--rc-font': "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif",
	'--rc-ink': '#323130',
	'--rc-ink-soft': '#605e5c',
	'--rc-bubble-in-ink': '#323130',
	'--rc-line': '#edebe9'
};

/**
 * Per-skin looks, tuned to each host's visual signature so the two are
 * unmistakably different at a glance:
 *
 *   bc  — Business Central web client: bright BC-blue chrome (outgoing bubbles,
 *         avatars, buttons all #0078D4), rounder Fluent radii, warm-grey canvas.
 *   scm — Finance & Operations: graphite/near-black chrome with the F&O blue
 *         accent, tighter square radii, cool-grey canvas.
 */
const SKIN_VARS: Record<AppearanceKey, Record<string, string>> = {
	bc: {
		'--rc-chrome': '#00838f',
		'--rc-chrome-dark': '#00646e',
		'--rc-accent': '#00838f',
		'--rc-accent-soft': '#4da7b0',
		'--rc-accent-faint': '#e6f3f4',
		'--rc-surface': '#faf9f8',
		'--rc-radius': '6px',
		'--rc-radius-bubble': '10px',
		'--rc-bubble-in-bg': '#f3f2f1',
		'--rc-hover': '#f0f8f9',
		'--rc-selected': '#d9edef'
	},
	scm: {
		'--rc-chrome': '#2266e3',
		'--rc-chrome-dark': '#1747a0',
		'--rc-accent': '#2266e3',
		'--rc-accent-soft': '#6f97ec',
		'--rc-accent-faint': '#edf2fd',
		'--rc-surface': '#f3f2f1',
		'--rc-radius': '2px',
		'--rc-radius-bubble': '4px',
		'--rc-bubble-in-bg': '#edebe9',
		'--rc-hover': '#f0f5fe',
		'--rc-selected': '#dce7fb'
	}
};

/**
 * Chat surfaces styled with tailwind/inline colors read these CSS variables with the
 * original hex as fallback, e.g. `var(--rc-chrome, #010644)` — so with no appearance
 * param the app looks exactly as before, and with one the chrome/accent colors follow
 * the selected skin.
 */
export function applyAppearanceCssVars() {
	const key = getAppearance();
	if (!key) return;

	const root = document.documentElement.style;
	Object.entries(SHARED_FLUENT_VARS).forEach(([name, value]) => root.setProperty(name, value));
	Object.entries(SKIN_VARS[key]).forEach(([name, value]) => root.setProperty(name, value));

	// Skin-wide font override: MUI/Tailwind set font-family via single-class
	// selectors, which this two-part selector outranks; without a skin the
	// attribute is absent and nothing changes.
	document.documentElement.dataset.appearance = key;
	if (!document.getElementById('rc-appearance-style')) {
		const style = document.createElement('style');
		style.id = 'rc-appearance-style';
		style.textContent = ':root[data-appearance] :not(svg):not(path) { font-family: var(--rc-font); }';
		document.head.appendChild(style);
	}
}
